import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Phone, Loader2, MessageCircleMore, Lock } from "lucide-react";
import { getUser } from "@/lib/user";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Révèle les numéros de l'annonceur (appel direct + WhatsApp) pour les membres abonnés. */
export const ContactAnnonceur = ({ annonceId, couleur }) => {
  const [numeros, setNumeros] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [verrouille, setVerrouille] = useState(false);
  const [motif, setMotif] = useState(null);
  const navigate = useNavigate();

  const revelerNumeros = async () => {
    const user = getUser();
    if (!user) {
      toast.error("Créez votre profil pour voir le numéro de l'annonceur.");
      navigate("/inscription");
      return;
    }
    setChargement(true);
    try {
      const res = await axios.get(`${API}/annonces/${annonceId}/contact`, {
        params: { viewer_id: user.id },
      });
      setNumeros(res.data.numeros);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (["ABONNEMENT_REQUIS", "ABONNEMENT_EXPIRE", "AUTRE_RUBRIQUE"].includes(detail?.code)) {
        setVerrouille(true);
        setMotif(detail.code);
        toast.error(
          detail.code === "ABONNEMENT_EXPIRE" ? "Abonnement expiré"
            : detail.code === "AUTRE_RUBRIQUE" ? "Rubrique non couverte"
            : "Numéros réservés aux abonnés",
          { description: detail.message },
        );
      } else {
        toast.error(detail?.message || "Numéro indisponible pour cette annonce.");
      }
    } finally {
      setChargement(false);
    }
  };

  if (verrouille) {
    return (
      <button
        onClick={() => navigate("/abonnement")}
        data-testid={`debloquer-numero-${annonceId}`}
        className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-700 transition-colors"
      >
        <Lock size={15} />
        {motif === "ABONNEMENT_EXPIRE" ? "Renouveler mon abonnement" : "Débloquer les numéros"}
      </button>
    );
  }

  if (!numeros) {
    return (
      <button
        onClick={revelerNumeros}
        disabled={chargement}
        data-testid={`voir-numero-${annonceId}`}
        className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-full text-white transition-opacity hover:opacity-90 disabled:opacity-60"
        style={{ backgroundColor: couleur }}
      >
        {chargement ? <Loader2 size={15} className="animate-spin" /> : <Phone size={15} />}
        {chargement ? "Ouverture…" : "Voir le numéro"}
      </button>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        className="space-y-2"
        data-testid={`numeros-annonceur-${annonceId}`}
      >
        {numeros.map((n) => (
          <div
            key={n.e164}
            className="flex items-center gap-2 rounded-2xl border-2 border-[#E5DFD5] px-3 py-2"
          >
            <Phone size={15} className="shrink-0 text-stone-500" />
            <a
              href={`tel:${n.tel}`}
              data-testid={`appeler-${n.e164}`}
              className="flex-1 min-w-0 truncate font-heading text-sm font-bold text-stone-900 tracking-wide hover:underline"
            >
              {n.affichage}
            </a>
            <a
              href={n.whatsapp}
              target="_blank"
              rel="noreferrer"
              data-testid={`whatsapp-${n.e164}`}
              title="Écrire sur WhatsApp"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-3 py-1.5 text-xs font-bold text-white transition-transform hover:scale-105"
            >
              <MessageCircleMore size={13} /> WhatsApp
            </a>
          </div>
        ))}
      </motion.div>
    </AnimatePresence>
  );
};
